import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
// root がこれを読むので、全部の頁の束に入る。@noiman/ui の大きな入口から読むと、レッスンの頁の部品 (図の 224KB の表と
// react-resizable-panels を含む塊) まで全部の頁で先に読まれていた。共有のボタンの入口から読む
import { ShareReferralContext, type ShareReferral } from "@noiman/ui/x-share-button";
import { useAuth } from "@/contexts/auth.context";

const SHARE_CODE_PATH = "/luagate/referral/share-code";

/**
 * 共有の釦 (XShareButton、NoteShareButton) に、今の受講生の紹介コードを渡す。
 * 釦が出たときに初めて 1 回だけ取りに行く。未ログインの人は取りに行かない。
 * 体験の人や luagate の外の人には、サーバーが 204 を返し、共有の住所は変わらない
 */
export function ReferralShareProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [code, setCode] = useState<{ userId: string; code: string } | null>(null);
  const asked = useRef<string | null>(null);

  const request = useCallback(() => {
    if (!userId || asked.current === userId) return;
    asked.current = userId;
    fetch(SHARE_CODE_PATH, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(async (res) => {
        // 204 は紹介コードを持たない人。頼み直さない。ほかの失敗は次に釦が出た時に頼み直す
        if (res.status === 204) return;
        if (res.status !== 200) {
          asked.current = null;
          return;
        }
        const body = (await res.json().catch(() => null)) as { code?: unknown } | null;
        if (typeof body?.code === "string") setCode({ userId, code: body.code });
      })
      .catch(() => {
        // 取れなければ共有の住所を変えないだけ。次に釦が出た時に頼み直す
        asked.current = null;
      });
  }, [userId]);

  const value = useMemo<ShareReferral>(
    () => ({ code: code && code.userId === userId ? code.code : null, request }),
    [code, userId, request],
  );
  return <ShareReferralContext.Provider value={value}>{children}</ShareReferralContext.Provider>;
}
